import { orderBy } from 'lodash';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { useLoader } from '../../../../../../../HOC/LoaderProvider';
import { Booth, Meetingroom, User } from '../../../../../../../interfaces/interfaces';
import { useSelector } from '../../../../../../../reduxToolkit/typedRedux';
import { formatUTCDate } from '../../../../../../../services/helper';
import { getAllQueueForBooth, getUsersMeetingJoinStatus } from '../../../../../../../services/services';
import VyDataGrid, { VyColDef } from '../../../../../../common/VyDataGrid';
import { VyExportButton } from '../../../../../../common/VyExportButton';
export interface JoinStatus {
  id: number;
  user: {
    name: string;
    email: string;
  };
  meetingroom: {
    contactperson: string;
    datetime_created: Date;
  };
}

export interface Report {
  datetime_created: Date;
  user: User;
  meetingroom: Meetingroom;
  response: string;
}
const BoothMeetingPartnerTab = ({ booth }: { booth: Booth }) => {
  const [reports, setReports] = useState<Report[]>([]);
  const [joinstatus, setJoinStatus] = useState<JoinStatus[]>([]);
  const user = useSelector((state) => state.common.user);
  const event = useSelector((state) => state.common.event);
  const { startLoader, stopLoader } = useLoader();
  const { t } = useTranslation();

  useEffect(() => {
    fetch();
  }, [booth]);

  async function fetch() {
    if (booth && booth._id) {
      startLoader();
      try {
        const data = await getAllQueueForBooth(booth._id);
        const sortedData = orderBy(data.data, [(obj) => new Date(obj.datetime_created)], ['desc']);
        setReports(sortedData);
        const join = await getUsersMeetingJoinStatus(booth._id);
        const joinStatus = join.map((dy: any, i: number) => ({ ...dy, _id: i }));
        setJoinStatus(joinStatus);
      } catch (err) {
        console.error(err);
      }
      stopLoader();
    }
  }
  const status: VyColDef[] = [
    {
      field: 'datetime_created',
      headerName: t('account:columns-date-time'),
      type: 'dateTime',
      textScroll: true,
      hideable: false,
      valueGetter: (value, row) => new Date(row.meetingroom.datetime_created as string),
      width: 200,
      sortable: false,
    },
    {
      field: 'email',
      type: 'string',
      textScroll: true,
      valueGetter: (value, row) => row.user.email,
      headerName: 'Email',
      width: 200,
      sortable: false,
    },
    {
      field: 'name',
      type: 'string',
      textScroll: true,
      headerName: 'Name',
      valueGetter: (value, row) => row.user.name,
      width: 200,
      sortable: false,
    },
    {
      field: 'meetingroom',
      type: 'string',
      textScroll: true,
      headerName: t('account:status-meetingroom'),
      valueGetter: (value, row) => row.meetingroom.contactperson,
      width: 200,
      sortable: false,
    },
    ...(event && event?.modules?.guestmanagement?.settings?.registrationFields?.length > 0
      ? event.modules.guestmanagement.settings.registrationFields
          .filter((element) => element.datetime_deleted === null && element.visibleToExhibitors !== false)
          .map((element) => ({
            field: element.label,
            headerName: element.label,
            valueGetter: (value: any, row: any) =>
              row.user.registration &&
              row.user.registration?.registrationFields &&
              row.user.registration?.registrationFields?.find((f: any) => f.field === element._id)?.value,
            width: 180,
            sortable: false,
            hide: true,
          }))
      : []),
  ];
  const columns: VyColDef[] = [
    {
      field: 'datetime_created',
      headerName: t('account:columns-date-time'),
      type: 'dateTime',
      hideable: true,
      valueFormatter: ({ value }) => new Date(value as string),
      width: 200,
      sortable: true,
    },
    {
      field: 'name',
      type: 'string',
      textScroll: true,
      headerName: 'Name',
      valueGetter: (value, row) => row.user.name,
      width: 200,
      sortable: false,
    },
    {
      field: 'email',
      type: 'string',
      textScroll: true,
      valueGetter: (value, row) => row.user.email,
      headerName: 'Email',
      width: 200,
      sortable: false,
    },
    {
      field: 'meetingroom',
      type: 'string',
      textScroll: true,
      valueGetter: (value, row) => row.meetingroom.contactperson,
      headerName: t('account:columns-meetingroom'),
      width: 200,
      sortable: false,
    },
    {
      field: 'response',
      valueGetter: (value, row) => {
        switch (row.response) {
          case 'accept':
            return t('account:columns-response-accept');
          case 'reject':
            return t('account:columns-response-decline');
          default:
            return row.response || t('account:columns-response-unknown');
        }
      },
      headerName: t('account:columns-response'),
      width: 200,
      sortable: true,
    },
    ...(event && event?.modules?.guestmanagement?.settings?.registrationFields?.length > 0
      ? event.modules.guestmanagement.settings.registrationFields
          .filter((element) => element.datetime_deleted === null && element.visibleToExhibitors !== false)
          .map((element) => ({
            field: element.label,
            headerName: element.label,
            valueGetter: (value: any, row: any) =>
              row.user.registration &&
              row.user.registration?.registrationFields &&
              row.user.registration?.registrationFields?.find((f: any) => f.field === element._id)?.value,
            width: 180,
            sortable: false,
            hide: true,
          }))
      : []),
  ];

  return (
    <div className="px-2 bg-white">
      <VyDataGrid
        rows={joinstatus.filter((join: any) => {
          if (user && user.role !== 'ORGANIZATION_EXTERNAL_SPEAKER') return true;
          return false;
        })}
        columns={status}
      />
      <div className="overflow-y-scroll" />
      <div className="mt-4 mb-0">
        <h1 className="text-xl font-medium">{t('account:p-meetingpartner')}</h1>
        <p className="text-sm">{t('account:event-join-status-subtitle')} </p>
      </div>
      <div className="flex justify-end">
        <VyExportButton
          csvURL={`/meeting/queue/booth/${booth._id}?resType=csv`}
          xlsxURL={`/meeting/queue/booth/${booth._id}?resType=xlsx`}
          forceModal
          axiosMethod="GET"
        />
      </div>
      <VyDataGrid
        rows={reports.filter((report: any) => {
          if (user && user.role !== 'ORGANIZATION_EXTERNAL_SPEAKER') return true;
          if (user && user.assignedMeetingroom && user.assignedMeetingroom.toString() === report.meetingroomid) return true;
          return false;
        })}
        columns={columns}
      />
    </div>
  );
};
export default BoothMeetingPartnerTab;
